import { useState, useEffect } from "react";
import { Activity, Play, Pause, Gauge, Volume2, VolumeX, LogOut, FileText } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { getSoundEnabled, setSoundEnabled } from "@/lib/sound";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Header({ aggregation, postCount, connected, mode, onRecapOpen, recapReady, pipelineHealth }) {
  const [time, setTime] = useState(new Date());
  const [streamStatus, setStreamStatus] = useState({ is_running: false, is_paused: false });
  const [isToggling, setIsToggling] = useState(false);
  const [soundOn, setSoundOn] = useState(getSoundEnabled);
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/v1/sentinel/stream/status`);
        setStreamStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch stream status:', err);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleToggleStream = async () => {
    if (isToggling) return;
    setIsToggling(true);

    try {
      const endpoint = streamStatus.is_running 
        ? `${BACKEND_URL}/api/v1/sentinel/stream/stop`
        : `${BACKEND_URL}/api/v1/sentinel/stream/start`;
      
      await axios.post(endpoint);
      const statusRes = await axios.get(`${BACKEND_URL}/api/v1/sentinel/stream/status`);
      setStreamStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to toggle stream:', err);
    } finally {
      setIsToggling(false);
    }
  };

  const avgScore = aggregation?.avg_score ?? 0;
  const scoreColor = avgScore > 0 ? '#22c55e' : avgScore < 0 ? '#ef4444' : '#94a3b8';

  const isStreamActive = streamStatus.is_running && !streamStatus.is_paused;
  const webhookActive = streamStatus.webhook?.active;

  let statusColor, statusText, statusLabel;
  if (streamStatus.is_paused === true) {
    statusColor = '#ef4444';
    statusText = 'STOPPED';
    statusLabel = 'Manual stop';
  } else if (webhookActive) {
    statusColor = '#22c55e';
    statusText = 'LIVE';
    statusLabel = 'Webhook active';
  } else if (isStreamActive) {
    statusColor = '#f59e0b';
    statusText = 'WATCHING';
    statusLabel = 'Webhook waiting';
  } else {
    statusColor = '#ef4444';
    statusText = 'DISCONNECTED';
    statusLabel = '';
  }

  return (
    <header
      data-testid="app-header"
      className="flex items-center justify-between px-3 sm:px-5 border-b"
      style={{
        height: '56px',
        backgroundColor: '#161b22',
        borderColor: '#30363d',
      }}
    >
      {/* Left side */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <img src="/logo-icon.png" alt="Macro Flare" className="shrink-0" style={{ width: 24, height: 24 }} />
        <h1
          data-testid="app-header-title"
          className="font-mono text-sm sm:text-base font-bold tracking-wide shrink-0"
          style={{ color: '#e6edf3' }}
        >
          <span className="hidden sm:inline">MACRO</span><span className="hidden sm:inline" style={{ color: '#ff6b2b' }}>FLARE</span>
          <span className="sm:hidden" style={{ color: '#ff6b2b' }}>MF</span>
        </h1>

        {/* Stream toggle */}
        {mode === 'live' && (
          <button
            onClick={handleToggleStream}
            disabled={isToggling}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded font-mono text-[10px] sm:text-xs font-medium transition-all shrink-0"
            style={{
              backgroundColor: isStreamActive ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)',
              color: isStreamActive ? '#22c55e' : '#ef4444',
              border: `1px solid ${isStreamActive ? 'rgba(34, 197, 94, 0.35)' : 'rgba(239, 68, 68, 0.35)'}`,
              opacity: isToggling ? 0.6 : 1,
              cursor: isToggling ? 'not-allowed' : 'pointer',
            }}
          >
            {isStreamActive ? <Pause size={11} /> : <Play size={11} />}
            <span className="hidden xs:inline">{isStreamActive ? 'ON' : 'OFF'}</span>
          </button>
        )}

        {/* Status dot + text */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={isStreamActive ? 'cw-live-dot' : ''}
            style={{
              display: 'inline-block',
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: statusColor,
            }}
          />
          <span
            data-testid="live-indicator"
            className="font-mono text-[10px] sm:text-xs"
            style={{ color: statusColor }}
          >
            {statusText}
          </span>
          {statusLabel && (
            <span className="hidden md:inline font-mono text-[10px]" style={{ color: '#484f58' }}>
              {statusLabel}
            </span>
          )}
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 shrink-0">
        {/* Posts/hr + Avg score */}
        <div className="hidden sm:flex items-center gap-2">
          <Activity size={14} style={{ color: '#8b949e' }} />
          <span className="font-mono text-xs" style={{ color: '#8b949e' }}>
            {streamStatus.is_paused ? '—' : (streamStatus.posts_per_hour ?? 0)}/hr
          </span>
          {postCount > 0 && (
            <span className="font-mono text-xs font-semibold" style={{ color: scoreColor }}>
              AVG {avgScore >= 0 ? '+' : ''}{avgScore.toFixed(1)}
            </span>
          )}
        </div>
        {/* Pipeline health indicator */}
        {pipelineHealth && (
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded font-mono text-[10px] shrink-0"
            data-testid="pipeline-health-indicator"
            style={{ border: '1px solid #21262d' }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
              backgroundColor: pipelineHealth.queue.total_queued > 4 ? '#ef4444' : pipelineHealth.queue.total_queued > 0 ? '#f59e0b' : '#22c55e',
              boxShadow: `0 0 4px ${pipelineHealth.queue.total_queued > 4 ? 'rgba(239,68,68,0.5)' : pipelineHealth.queue.total_queued > 0 ? 'rgba(245,158,11,0.5)' : 'rgba(34,197,94,0.5)'}`,
            }} />
            <span style={{ color: '#484f58' }}>Q:{pipelineHealth.queue.total_queued}</span>
            {pipelineHealth.throughput.last_5m > 0 && (
              <span style={{ color: '#484f58' }}>| {pipelineHealth.throughput.last_5m}/5m</span>
            )}
          </div>
        )}
        <button
          data-testid="recap-btn"
          onClick={onRecapOpen}
          className="flex items-center gap-1 px-2 py-1 rounded font-mono text-[10px] sm:text-xs transition-colors duration-150 shrink-0"
          style={{
            color: '#ff6b2b',
            border: '1px solid rgba(255,107,43,0.3)',
            backgroundColor: 'rgba(255,107,43,0.06)',
          }}
          title="Session Recap"
        >
          <FileText size={13} />
          <span className="hidden sm:inline">RECAP</span>
          {recapReady && (
            <span className="inline-block w-1.5 h-1.5 rounded-full ml-0.5"
              style={{ backgroundColor: '#22c55e', boxShadow: '0 0 4px rgba(34,197,94,0.6)' }} />
          )}
        </button>
        <button
          data-testid="sound-toggle"
          onClick={() => {
            const next = !soundOn;
            setSoundOn(next);
            setSoundEnabled(next);
          }}
          className="flex items-center px-1.5 py-1 rounded font-mono text-[10px] transition-colors duration-150 shrink-0"
          style={{
            color: soundOn ? '#8b949e' : '#484f58',
            border: '1px solid #30363d',
          }}
          title={soundOn ? 'Sound alerts on' : 'Sound alerts off'}
        >
          {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
        </button>
        <button
          data-testid="dashboard-link"
          onClick={() => navigate(isDashboard ? '/' : '/dashboard')}
          className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded font-mono text-[10px] sm:text-xs transition-colors duration-150 shrink-0"
          style={{
            backgroundColor: isDashboard ? 'rgba(34, 197, 94, 0.12)' : 'transparent',
            color: isDashboard ? '#22c55e' : '#8b949e',
            border: `1px solid ${isDashboard ? 'rgba(34, 197, 94, 0.35)' : '#30363d'}`,
          }}
          title={isDashboard ? 'Back to Feed' : 'System Dashboard'}
        >
          <Gauge size={13} />
          {isDashboard ? 'FEED' : 'OPS'}
        </button>
        <time
          data-testid="app-header-timestamp"
          className="hidden sm:block font-mono text-xs"
          style={{ color: '#8b949e' }}
        >
          {time.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })} ET</time>
        <button
          data-testid="logout-btn"
          onClick={() => {
            localStorage.removeItem('macroflare_token');
            window.location.reload();
          }}
          className="flex items-center px-1.5 py-1 rounded font-mono text-[10px] transition-colors duration-150 shrink-0 hover:bg-red-500/10"
          style={{
            color: '#484f58',
            border: '1px solid #30363d',
          }}
          title="Log out"
        >
          <LogOut size={13} />
        </button>
      </div>
    </header>
  );
}
